/**
 * TanStack Query hooks for Reporting
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { reportService } from '../services/reporting/report-service';
import type {
  ExportPreferences,
  ReportGenerationInput,
  ReportType,
} from '../types/report.types';

const REPORTS_KEY = ['reports'];
const EXPORT_PREFS_KEY = ['report-export-preferences'];

const DEFAULT_EXPORT_PREFS: ExportPreferences = {
  defaultFormat: 'pdf',
  defaultDatePreset: 'this_month',
  customStartDate: null,
  customEndDate: null,
};

export function useReports(limit = 5, offset = 0) {
  return useQuery({
    queryKey: [...REPORTS_KEY, limit, offset],
    queryFn: async () => {
      const res = await reportService.listReports({ limit, offset });
      if (res.error) throw new Error(res.error);
      return res.data!;
    },
  });
}

export function useExportPreferences() {
  return useQuery({
    queryKey: EXPORT_PREFS_KEY,
    queryFn: async () => {
      const res = await reportService.getExportPreferences();
      if (res.error || !res.data) return DEFAULT_EXPORT_PREFS;
      return res.data;
    },
    placeholderData: DEFAULT_EXPORT_PREFS,
  });
}

export function useGenerateReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ReportGenerationInput) => reportService.generateReport(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: REPORTS_KEY }),
  });
}

export function useQuickExport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (params: { reportType: ReportType; requiresFilters?: boolean }) =>
      reportService.quickExport(params.reportType, undefined, {
        requiresFilters: params.requiresFilters,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: REPORTS_KEY }),
  });
}

export function useDownloadReport() {
  return useMutation({
    mutationFn: (reportId: string) => reportService.getDownloadUrl(reportId),
  });
}

export function useDeleteReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (reportId: string) => reportService.deleteReport(reportId),
    onSuccess: () => qc.invalidateQueries({ queryKey: REPORTS_KEY }),
  });
}
